import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Grid, IconButton } from '@mui/material'
import { makeStyles } from '@mui/styles'
import {
  ButtonWithLoader,
  type CustomFieldValidatorState,
  ErrorTooltip,
  type Field,
  Text,
  useFields,
  Validators,
} from '@pbt/pbt-ui-components'
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@pbt/pbt-ui-components/src/icons'

import type {
  Contact,
  ContactSearchResult,
} from '~/api/graphql/generated/types'
import { isManualContactId } from '~/utils/pmrUtils'

import AddNewPracticeForm from './AddNewPracticeForm'
import { ClinicBrief } from './ClinicBrief'
import ClinicSearch from './ClinicSearch'

const useStyles = makeStyles(
  (theme) => ({
    sectionHeading: {
      fontSize: '1.4rem',
    },
    errorTooltip: {
      width: 'fit-content',
      maxWidth: 'none',
    },
    addAnotherButton: {
      marginTop: theme.spacing(2),
      width: '50%',
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '2px',
      },
    },
    clinicItem: {
      marginBottom: theme.spacing(1),
    },
    clinicItemWrapper: {
      display: 'inline-flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
    },
    actionIcon: {
      padding: 0,
      color: theme.colors.grayGray1,
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '2px',
        borderRadius: 4,
      },
    },
  }),
  { name: 'PreviousPracticesForm' },
)

export interface PreviousPracticesFormProps {
  previousClinics: Field
}

const PreviousPracticesForm = ({
  previousClinics,
}: PreviousPracticesFormProps) => {
  const classes = useStyles()
  const { t } = useTranslation(['Common', 'TimeTable'])

  const [isAddNewPracticeFormOpen, setIsAddNewPracticeFormOpen] =
    useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(
    previousClinics.value.length === 0,
  )
  const [isSearching, setIsSearching] = useState(false)
  const [editingClinicId, setEditingClinicId] = useState<string | null>(null)

  const validatePreviousPracticeName = ({ value }: CustomFieldValidatorState) =>
    !isAddNewPracticeFormOpen || Validators.notEmptyFormField(value)

  const validateStreetAddress = ({ value }: CustomFieldValidatorState) =>
    !isAddNewPracticeFormOpen || Validators.notEmptyFormField(value)

  const validateCity = ({ value }: CustomFieldValidatorState) =>
    !isAddNewPracticeFormOpen || Validators.notEmptyFormField(value)

  const validateState = ({ value }: CustomFieldValidatorState) =>
    !isAddNewPracticeFormOpen || Validators.notEmptyFormField(value)

  const validatePhoneNumber = ({ value }: CustomFieldValidatorState) => {
    if (!isAddNewPracticeFormOpen) return true
    if (!Validators.notEmptyFormField(value)) return false
    return Validators.isPhoneValid(value)
  }

  const {
    fields: formFields,
    reset: resetFormFields,
    validate: validateFormFields,
  } = useFields(
    [
      {
        name: 'previousPracticeName',
        label: t('TimeTable:PMR_CONSENT.PREVIOUS_PRACTICE_NAME_LABEL'),
        initialValue: '',
        validators: [
          {
            validator: validatePreviousPracticeName,
            validatorName: 'required',
          },
        ],
        messages: {
          required: t('TimeTable:PMR_CONSENT.PLEASE_ENTER_PRACTICE_NAME_ERROR'),
        },
      },
      {
        name: 'streetAddress',
        label: t('TimeTable:PMR_CONSENT.STREET_ADDRESS_LABEL'),
        initialValue: '',
        validators: [
          {
            validator: validateStreetAddress,
            validatorName: 'required',
          },
        ],
        messages: {
          required: t(
            'TimeTable:PMR_CONSENT.PLEASE_ENTER_STREET_ADDRESS_ERROR',
          ),
        },
      },
      {
        name: 'aptSuiteUnitBuilding',
        label: t('TimeTable:PMR_CONSENT.APT_SUITE_UNIT_BUILDING_LABEL'),
        initialValue: '',
      },
      {
        name: 'city',
        label: t('TimeTable:PMR_CONSENT.CITY_LABEL'),
        initialValue: '',
        validators: [
          {
            validator: validateCity,
            validatorName: 'required',
          },
        ],
        messages: {
          required: t('TimeTable:PMR_CONSENT.PLEASE_ENTER_CITY_ERROR'),
        },
      },
      {
        name: 'state',
        label: t('TimeTable:PMR_CONSENT.STATE_LABEL'),
        initialValue: '',
        validators: [
          {
            validator: validateState,
            validatorName: 'required',
          },
        ],
        messages: {
          required: t('TimeTable:PMR_CONSENT.PLEASE_ENTER_STATE_ERROR'),
        },
      },
      {
        name: 'phoneNumber',
        label: t('TimeTable:PMR_CONSENT.PHONE_NUMBER_LABEL'),
        initialValue: '',
        validators: [
          {
            validator: validatePhoneNumber,
            validatorName: 'required',
          },
        ],
        messages: {
          required: t('TimeTable:PMR_CONSENT.PLEASE_ENTER_PHONE_NUMBER_ERROR'),
        },
      },
      {
        name: 'emailAddress',
        label: t('TimeTable:PMR_CONSENT.EMAIL_ADDRESS_LABEL'),
        initialValue: '',
      },
    ],
    false,
  )

  const {
    aptSuiteUnitBuilding,
    city,
    emailAddress,
    phoneNumber,
    previousPracticeName,
    state,
    streetAddress,
  } = formFields

  // Select contact brings search clinics which has no contact id yet, so we that is why we are looking for kyriosId instead
  const handleSelectContact = (contact: Contact | ContactSearchResult) => {
    const currentContacts = previousClinics.value as Partial<Contact>[]
    if (!currentContacts.some((c) => c.kyriosId === contact.kyriosId)) {
      previousClinics.setValue([...currentContacts, contact])
    }
    setIsSearchOpen(false)
    setIsSearching(false)
  }

  const handleAddAnotherPractice = () => {
    setIsSearchOpen(true)
  }

  const handleResultsVisibilityChange = (visible: boolean) => {
    setIsSearching(visible)
  }

  const handleOpenForm = () => {
    setIsSearchOpen(false)
    setIsAddNewPracticeFormOpen(true)
  }

  const handleAddPractice = async () => {
    setIsSearching(false)
    if (validateFormFields()) {
      const currentContacts = previousClinics.value as Partial<Contact>[]

      if (editingClinicId !== null) {
        const updatedContacts = currentContacts.map((contact) =>
          contact.id === editingClinicId
            ? {
                ...contact,
                name: previousPracticeName.value,
                phone: phoneNumber.value,
                address: streetAddress.value,
                suite: aptSuiteUnitBuilding.value || undefined,
                city: city.value,
                state: state.value,
                email: emailAddress.value || undefined,
              }
            : contact,
        )
        previousClinics.setValue(updatedContacts)
        setEditingClinicId(null)
      } else {
        const newContact: Partial<Contact> = {
          id: crypto.randomUUID(),
          name: previousPracticeName.value,
          phone: phoneNumber.value,
          address: streetAddress.value,
          suite: aptSuiteUnitBuilding.value || undefined,
          city: city.value,
          state: state.value,
          email: emailAddress.value || undefined,
        }
        previousClinics.setValue([...currentContacts, newContact])
      }

      resetFormFields()
      setIsAddNewPracticeFormOpen(false)
    }
  }

  const handleCancelAddPractice = () => {
    resetFormFields()
    setEditingClinicId(null)
    setIsAddNewPracticeFormOpen(false)
  }

  const handleDeleteContact = (contactId: string) => {
    const currentContacts = previousClinics.value as Partial<Contact>[]
    const updatedContacts = currentContacts.filter((c) =>
      'id' in c ? c.id !== contactId : c.kyriosId !== contactId,
    )
    previousClinics.setValue(updatedContacts)
    if (updatedContacts.length === 0) {
      setIsSearchOpen(true)
    }
  }

  // Manual contacts have ids generated by crypto.randomUUID() and are editable,
  // while contacts from search results have kyriosId and are not editable
  const handleEditManualContact = (contact: Partial<Contact>) => {
    previousPracticeName.setValue(contact.name || '')
    streetAddress.setValue(contact.address || '')
    aptSuiteUnitBuilding.setValue(contact.suite || '')
    city.setValue(contact.city || '')
    state.setValue(contact.state || '')
    phoneNumber.setValue(contact.phone || '')
    emailAddress.setValue(contact.email ?? '')

    setEditingClinicId(contact.id ?? null)
    setIsSearchOpen(false)
    setIsAddNewPracticeFormOpen(true)
  }

  useEffect(() => {
    if (
      isAddNewPracticeFormOpen &&
      previousClinics.open &&
      !previousClinics.valid
    ) {
      validateFormFields()
    }
  }, [
    isAddNewPracticeFormOpen,
    previousClinics.open,
    previousClinics.valid,
    validateFormFields,
  ])

  const contactsList = previousClinics.value as Partial<Contact>[]

  return (
    <Grid container direction="column" mb={3}>
      {(!isAddNewPracticeFormOpen || contactsList.length > 0) && (
        <Grid item>
          <Text className={classes.sectionHeading} pb={1} variant="h2">
            {contactsList.length > 0
              ? t('TimeTable:PMR_CONSENT.PREVIOUS_PRACTICES_TITLE')
              : t('TimeTable:PMR_CONSENT.FIND_PREVIOUS_PRACTICE_TITLE')}
          </Text>
        </Grid>
      )}
      {contactsList.length > 0 && (
        <Grid container direction="column">
          {contactsList.map((contact) => {
            const contactId = (contact.id ?? contact.kyriosId) as string
            return (
              <Grid item className={classes.clinicItem} key={contactId}>
                <div className={classes.clinicItemWrapper}>
                  <ClinicBrief contact={contact} />
                  {isManualContactId(contactId) && (
                    <IconButton
                      aria-label={t('Common:EDIT_CLINIC')}
                      className={classes.actionIcon}
                      size="small"
                      onClick={() => handleEditManualContact(contact)}
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                  <IconButton
                    aria-label={t('Common:DELETE_CLINIC')}
                    className={classes.actionIcon}
                    disabled={
                      contactId != null && editingClinicId === contactId
                    }
                    size="small"
                    // We have one or another, during search creation we rely on kyriosId
                    onClick={() => handleDeleteContact(contactId)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              </Grid>
            )
          })}
        </Grid>
      )}
      {!isAddNewPracticeFormOpen && isSearchOpen && (
        <ErrorTooltip
          classes={{
            errorTooltip: classes.errorTooltip,
          }}
          message={previousClinics.message}
          open={previousClinics.open && !previousClinics.valid && !isSearching} // !isSearching so it doesn't appear ontop of search results list
          placement="bottom"
        >
          <Grid item>
            <ClinicSearch
              onAddNewPractice={handleOpenForm}
              onResultsVisibilityChange={handleResultsVisibilityChange}
              onSelectContact={handleSelectContact}
            />
          </Grid>
        </ErrorTooltip>
      )}
      {!isAddNewPracticeFormOpen && !isSearchOpen && (
        <ButtonWithLoader
          className={classes.addAnotherButton}
          color="secondary"
          onClick={handleAddAnotherPractice}
        >
          {t('TimeTable:PMR_CONSENT.ADD_ANOTHER_PRACTICE')}
        </ButtonWithLoader>
      )}
      {isAddNewPracticeFormOpen && (
        <AddNewPracticeForm
          fields={{
            aptSuiteUnitBuilding,
            city,
            emailAddress,
            phoneNumber,
            previousPracticeName,
            state,
            streetAddress,
          }}
          isFormOpen={isAddNewPracticeFormOpen}
          isValidationTriggeredExternally={
            previousClinics.open && !previousClinics.valid
          }
          submitLabel={
            editingClinicId !== null
              ? t('TimeTable:PMR_CONSENT.UPDATE_PRACTICE_BUTTON')
              : undefined
          }
          title={
            editingClinicId !== null
              ? t('TimeTable:PMR_CONSENT.EDIT_PRACTICE_TITLE')
              : undefined
          }
          onCancel={handleCancelAddPractice}
          onSubmit={handleAddPractice}
        />
      )}
    </Grid>
  )
}

export default PreviousPracticesForm
